import React from 'react';
import { Check, ArrowRight, Users, Clock, MapPin, BookOpen, ChevronRight, Banknote } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function FirstTimeManager() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    badge: 'Leadership development',
    title: 'First-Time',
    titleHighlight: 'Manager',
    subtitle: 'The thing nobody told you about becoming a manager.',
    duration: '2 days',
    audience: 'New managers and team leads',
    format: 'On-site',
    bookCta: 'Get in touch',
    gapTitle: 'The gap nobody prepares you for',
    gapP1: 'Most people become managers for the first time because they were outstanding specialists - good at their craft, their technical role, whatever it was. That expertise delivers results and helps earn the position. But management is a different job, not a continuation of what came before.',
    gapP2: 'The skills that made you an outstanding specialist - focus, personal accountability, knowing the right answers - are not what is required of a manager. Especially not today, when change and technological advancement are rapid. The manager needs to create conditions where others can do their best work. That is a fundamentally different orientation, and most people step into it with little or no preparation.',
    gapP3: "Gallup's research shows that most organisations assign people to management roles based on their performance as individuals, when what weighs more heavily should be the qualities that predict good management ability. The result is that many managers have to figure out on their own what works in the new role - often at the expense of the people they are supposed to lead.",
    frameworkTitle: 'What the two days cover',
    frameworkSubtitle: 'The programme is built around the two challenges that matter most in the first two years of management.',
    day1Title: 'Day 1 - Leading yourself',
    day1Intro: 'Before we can lead others, we need to be able to lead ourselves. To understand what happens inside us when we are under stress and pressure. To understand what behaviour emerges in us when things get hard - versus how we intend to show up.',
    day1Items: ["The transition: what changes when you become a manager, and what doesn't", 'The trap: how the skills that got you here can work against you from here on', 'The four selves of the leader: a framework for self-awareness under pressure', 'Developing a coaching approach: staying curious a little longer before jumping in to solve things for people', 'Self-regulation: how we show up from our best when the heat is on'],
    day2Title: 'Day 2 - Leading others',
    day2Intro: 'How teams actually develop, what psychological safety requires from a leader, and the communication skills that make difficult conversations more likely - when it would be easier to avoid them.',
    day2Items: ['How groups become teams - and why many never achieve it (Wheelan, Tuckman)', 'What psychological safety requires from leaders (Edmondson)', 'Feedback that develops people rather than trains them into compliance', '1:1 conversations as a development tool - not just a status report', 'Difficult conversations: how to name and tackle what is actually happening'],
    notCoveredTitle: 'What this programme is not',
    notCovered: 'This is not a course on HR administration, labour law, or the operational side of management. Those areas matter and resources for learning them are easy to find. This programme is about the human side of the role - which is where most new managers actually struggle, and where the research consistently identifies the largest gap.',
    formatTitle: 'Format',
    formatDesc: "Two full days, on-site. The days weave together shorter inputs - concepts, tools and frameworks - with practice sessions where methods are tested and we work through real situations, often drawn directly from participants' own contexts. The ratio is roughly one third input, two thirds practice and discussion. Maximum 12 participants.",
    formatNote: 'The programme can also be developed as an in-house option for workplaces wanting to offer this to a group of new or aspiring managers. Get in touch to discuss this as an option and receive a quote.',
    seriesNote: 'The ideas and thinking behind this programme are developed in the Lessons of Leadership series - publishing soon on Substack.',
    grantsTitle: 'Grants and funding',
    grants: [{
      text: 'You may be eligible for a grant covering course fees from your trade union. Check with your union for more details.'
    }, {
      text: 'The Icelandic Labour Market Board (Vinnumálastofnun) provides unemployed individuals with a grant of up to 75% of the course fee.'
    }, {
      text: "Most companies are eligible for up to 90% funding for employee training through the Vocational Training Fund (Starfsmenntunarsjóður). This funding is available regardless of the employees' credits in the fund.",
      link: 'https://island.is',
      linkText: 'island.is'
    }],
    forWhomTitle: 'Who this is for',
    forWhom: ['People who have recently taken on a management role (0-2 years)', 'Specialists and technical leads who have taken on people responsibility', 'Founders who have moved from doing the work to managing others who now do it', 'Experienced managers who want to sharpen the foundations with fresh eyes'],
    ctaTitle: "Let's talk",
    ctaDesc: 'Tell me about your situation - your role, your team, and what you are trying to work on. We can figure out from there whether this programme fits.',
    ctaCta: 'Get in touch'
  };
  const is = {
    badge: 'Leiðtogaþróun',
    title: 'Stjórnandi',
    titleHighlight: 'í fyrsta sinn',
    subtitle: 'Hvað enginn sagti þér um að verða stjórnandi.',
    duration: '2 dagar',
    audience: 'Nýjir stjórnendur og teymisstjórar',
    format: 'Á staðnum',
    bookCta: 'Hafa samband',
    gapTitle: 'Stökkið sem enginn undirbjó þig fyrir',
    gapP1: 'Flestir verða stjórnendur í fyrsta sinn af üess vegna að þau voru framúrskarandi sérfræðingar áður - góð í sínu fagi, tæknihlutverki eða handverki, hvað sem það var. Sú færni og sérþekking skilar árangri og hjálpar til að fá stöðuna. En stjórnun er annað starf, ekki framhald af üess sem var áður.',
    gapP2: 'Færnin sem gerði þig framúrskarandi sérfræðing - einbeiting, persónuleg ábyrgð, að vita réttu svörin - er ekki það sem krafist er af stjórnanda. Sérstaklega ekki í dag, þegar breytingar og tækniframfarir eru hraðar. Stjórnandinn þurfa að skapa aðstæður þar sem aðrir geta unnið sitt besta. Það er gjörólíkt sjónarhorn á starfið, og flestir stíga inn í það með litla sem enga undirbúningsvinnu.',
    gapP3: 'Rannsóknir Gallup sýna að flest fyrirtæki veita fólki stjórnunarhlutverk útfrá frammistuðu þess sem einstaklinga, þegar það vegur þyngra að hafa eiginleika sem spá fyrir um góða stjórnunarhæfni. Niðurstaðan er að margir stjórnendur þurfa að finna sjálf út hvað virkar í nýja hlutverkinu - oft á kostnað starfsfólksins sem þau á að leiða.',
    frameworkTitle: 'Yfirlit námskeiðs',
    frameworkSubtitle: 'Námskeiðið er byggð á tveimur megin áskorunum sem skipta mestu máli í fyrstu tveimur árum stjórnunar.',
    day1Title: 'Dagur 1 - Að leiða sjálfan sig',
    day1Intro: 'Áður en við getum leitt aðra þurfa við að geta leitt okkur sjálf. Að skilja hvað gerist innra með okkur þegar við erum undir stressi og álagi. Að skilja hvaða hegðun framkallast hjá okkur þegar viðfangsefnin eru erfið - vs hvernig við ætlum að vera.',
    day1Items: ['Umbreytingin: hvað breytist þegar þú verður stjórnandi, og hvað breytist ekki', 'Gildran: hvernig færnin sem færði þig hingað getur virkað gegn þér heðan í frá', 'Fjögur sjálf leiðtogans: rammi fyrir sjálfsvitund undir álagi', 'Að þróa markþjálfandi nálgun: að vera forvitinn aðeins lengur áður en stökkum til að leysa hluti fyrir fólkið okkar', 'Sjálfsstjórn: hvernig við mætum til leiks útfrá okkar besta þegar hitnar í kolunum'],
    day2Title: 'Dagur 2 - Að leiða aðra',
    day2Intro: 'Hvernig teymi þroskast raunverulega, hvað sálfræðilegt öryggi krefst af leiðtoga, og færnin í samskiptum sem gerir krefjandi samtölin líklegri þegar auðveldara er að forðast þau.',
    day2Items: ['Hvernig hópar verða teymi - og hvers vegna margir ná þess aldrei (Wheelan, Tuckman)', 'Hvað sálfræðilegt öryggi krefst af leiðtogum (Edmondson)', 'Endurgjöf sem þróar fólk fremur en agar til hlýðni', '1á1 samtöl sem verkfæri til að þroskast - ekki bara stöðurapport', 'Krefjandi samtöl: hvernig á að nefna og tækla það sem raunverulega er að gerast'],
    notCoveredTitle: 'Hvað þetta námskeið er ekki',
    notCovered: 'Þetta er ekki námskeið um umsjón mannauðs, vinnurétt eða rekstrarlegar hliðar starfsins. Þær hliðar skipta máli og einfalt að finna úrræði til að læra þær. Þetta námskeið fjallar um mannlegu hliðina á hlutverkinu - sem eru þær sem flestir nýjir stjórnendur glíma mest við í raunveruleikanum, og þar sem rannsóknirnar benda á stærsta bilið er til staðar.',
    formatTitle: 'Snið',
    formatDesc: 'Tveir heilir dagar, á staðnum. Dagarnir vefa saman styttri fyrirlestrum sem miðla þekk ingu, tækjum og tólum með æfingarlotum þar sem prufukeyraðar eru aðferðir og við vinnum með raundæmi, oftast mikið byggð á aðstæðum frá þátttakendum sjálfum. Hlutfallið er gróflega 1/3 hluti fræðsla, 2/3 hlutar æfingar og umræður. Hámark 12 þátttakendur.',
    formatNote: 'Hægt er að útfæra námskeiðið sem innanhús-valkost fyrir vinnustaði sem vilja bjóða uppá þetta fyrir hóp verðandi eða nýra stjórnenda. Hafið samband til að ræða þetta sem valkost og fá tilboð.',
    seriesNote: 'Þær hugmyndir og hugsun sem liggur að baki þessu námskeiði eru þróaðar í greinaseríunni “Lessons of Leadership” sem verður fljótlega birt á Substack.',
    grantsTitle: 'Styrktarmöguleikar',
    grants: [{
      text: 'Þú getur átt rétt á styrk frá stafrænu sjóði þins staðfagfælna til að greiða námskeiðsgjald. Kannaðu möguleikana hjá þinþim stafræna sjóði.'
    }, {
      text: 'Vinnumalástofnun veitir atvinnulausum einstæklingum styrk upp að 75% af námskeiðsgjaldi.'
    }, {
      text: 'Flæst fyrirtæki éigu rétt á allt að 90% endurgreiðslu útgöfna fyrir starfsmenntun starfsmænð gegnum Starfsmenntunarsjóð. Þessi endurgreisla er veitt óhæð inðeignum starfsmænðanna í sjóðinum.',
      link: 'https://island.is',
      linkText: 'island.is'
    }],
    forWhomTitle: 'Fyrir hvern?',
    forWhom: ['Fólk sem hefur nýlega tekið að sér stjórnandahlutverk (0-2 ár)', 'Sérfræðingar og tæknileiðtogar sem hafa tekið á sig mannaforráð', 'Stofnendur sem hafa farið frá að gera verkið yfir í að stjórna öðrum sem nú gera verkið', 'Reyndir stjórnendur sem vilja skerpa grunninn með ferskum augum'],
    ctaTitle: 'Við skulum tala saman',
    ctaDesc: 'Segðu mér frá stöðu þínni - hlutverkinu, teyminu og hvað þú ert að reyna að vinna að. Við getum fundið út þadan frá hvort þetta námskeið hentar.',
    ctaCta: 'Hafa samband'
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-765">
      <div className="pt-12 pb-24" data-id="element-766">
        <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 text-center" data-id="element-767">
          <DecorativeShape type="blob" color="bg-teal/10" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-768" />
          <DecorativeShape type="circle" color="bg-coral/10" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-769" />
          <Animate data-id="element-770">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-771">
              {c.badge}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-id="element-772">
              {c.title} <span className="text-teal" data-id="element-773">{c.titleHighlight}</span>
            </h1>
            <p className="text-xl text-gray-500 italic mb-8" data-id="element-774">{c.subtitle}</p>
            <div className="flex flex-wrap justify-center gap-3 text-sm font-medium text-gray-500 mb-10" data-id="element-775">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-776">
                <Clock size={16} className="text-teal" data-id="element-777" />
                <span data-id="element-778">{c.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-779">
                <Users size={16} className="text-teal" data-id="element-780" />
                <span data-id="element-781">{c.audience}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-782">
                <MapPin size={16} className="text-teal" data-id="element-783" />
                <span data-id="element-784">{c.format}</span>
              </div>
            </div>
            <Link to="/contact" data-id="element-785">
              <Button size="lg" rightIcon={<ArrowRight size={20} data-id="element-787" />} data-id="element-786">
                {c.bookCta}
              </Button>
            </Link>
          </Animate>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-788">
          <Animate data-id="element-789">
            <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-790">
              {c.gapTitle}
            </h2>
            <div className="space-y-4 text-lg text-gray-600 leading-relaxed" data-id="element-791">
              <p data-id="element-792">{c.gapP1}</p>
              <p data-id="element-793">{c.gapP2}</p>
              <p className="text-base text-gray-500 border-l-4 border-teal/30 pl-4" data-id="element-794">
                {c.gapP3}
              </p>
            </div>
          </Animate>
        </section>

        <section className="bg-gray-50 py-16 mb-20" data-id="element-795">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-796">
            <Animate className="mb-10" data-id="element-797">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-798">
                {c.frameworkTitle}
              </h2>
              <p className="text-gray-500" data-id="element-799">{c.frameworkSubtitle}</p>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-800">
              <Animate delay={0} data-id="element-801">
                <Card className="p-8 border-2 border-teal/20 h-full flex flex-col" data-id="element-802">
                  <div className="flex items-center gap-3 mb-4" data-id="element-803">
                    <div className="w-10 h-10 rounded-full bg-teal text-white font-bold flex items-center justify-center flex-shrink-0" data-id="element-804">
                      1
                    </div>
                    <h3 className="font-bold text-xl text-charcoal" data-id="element-805">
                      {c.day1Title}
                    </h3>
                  </div>
                  <p className="text-gray-500 text-sm mb-5 leading-relaxed" data-id="element-806">
                    {c.day1Intro}
                  </p>
                  <div className="space-y-3 flex-1" data-id="element-807">
                    {c.day1Items.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-808">
                        <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-809">
                          <Check size={12} strokeWidth={3} data-id="element-810" />
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed" data-id="element-811">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </Animate>
              <Animate delay={0.1} data-id="element-812">
                <Card className="p-8 border-2 border-coral/20 h-full flex flex-col" data-id="element-813">
                  <div className="flex items-center gap-3 mb-4" data-id="element-814">
                    <div className="w-10 h-10 rounded-full bg-coral text-white font-bold flex items-center justify-center flex-shrink-0" data-id="element-815">
                      2
                    </div>
                    <h3 className="font-bold text-xl text-charcoal" data-id="element-816">
                      {c.day2Title}
                    </h3>
                  </div>
                  <p className="text-gray-500 text-sm mb-5 leading-relaxed" data-id="element-817">
                    {c.day2Intro}
                  </p>
                  <div className="space-y-3 flex-1" data-id="element-818">
                    {c.day2Items.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-819">
                        <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-820">
                          <Check size={12} strokeWidth={3} data-id="element-821" />
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed" data-id="element-822">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </Animate>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-823">
          <div className="space-y-8" data-id="element-824">
            <Animate data-id="element-825">
              <Card className="p-6 border-l-4 border-gray-300 bg-gray-50" data-id="element-826">
                <h3 className="font-bold text-charcoal mb-2" data-id="element-827">
                  {c.notCoveredTitle}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-828">{c.notCovered}</p>
              </Card>
            </Animate>
            <Animate data-id="element-829">
              <div data-id="element-830">
                <h2 className="text-2xl font-bold text-charcoal mb-4" data-id="element-831">
                  {c.formatTitle}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-832">
                  {c.formatDesc}
                </p>
                <p className="text-sm text-gray-500 italic border-l-4 border-teal/30 pl-4" data-id="element-833">
                  {c.formatNote}
                </p>
              </div>
            </Animate>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-834">
          <Animate data-id="element-835">
            <Card className="p-6 border-l-4 border-teal bg-teal/5" data-id="element-836">
              <div className="flex items-start gap-3" data-id="element-837">
                <BookOpen size={20} className="text-teal flex-shrink-0 mt-0.5" data-id="element-838" />
                <p className="text-gray-700" data-id="element-839">
                  {c.seriesNote}{' '}
                  <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" className="text-teal font-semibold hover:underline" data-id="element-840">
                    Substack <ChevronRight size={14} className="inline" data-id="element-841" />
                  </a>
                </p>
              </div>
            </Card>
          </Animate>
        </section>

        {/* Grants */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-842">
          <Animate data-id="element-843">
            <div className="flex items-center gap-3 mb-5" data-id="element-844">
              <Banknote size={22} className="text-teal flex-shrink-0" data-id="element-845" />
              <h2 className="text-2xl font-bold text-charcoal" data-id="element-846">
                {c.grantsTitle}
              </h2>
            </div>
            <div className="space-y-3" data-id="element-847">
              {c.grants.map((g, i) => <div key={i} className="flex items-start gap-3" data-id="element-848">
                  <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-849">
                    <Check size={12} strokeWidth={3} data-id="element-850" />
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm" data-id="element-851">
                    {g.text}
                    {g.link && <>
                        {' '}
                        <a href={g.link} target="_blank" rel="noopener noreferrer" className="text-teal underline hover:text-teal/80" data-id="element-852">
                          {g.linkText}
                        </a>
                      </>}
                  </p>
                </div>)}
            </div>
          </Animate>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-853">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10" data-id="element-854">
            <div className="lg:col-span-3" data-id="element-855">
              <Animate data-id="element-856">
                <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-857">
                  {c.forWhomTitle}
                </h2>
                <div className="space-y-3" data-id="element-858">
                  {c.forWhom.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-859">
                      <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-860">
                        <Check size={13} strokeWidth={3} data-id="element-861" />
                      </div>
                      <span className="text-gray-700 leading-relaxed" data-id="element-862">
                        {item}
                      </span>
                    </div>)}
                </div>
              </Animate>
            </div>
            <div className="lg:col-span-2" data-id="element-863">
              <Animate data-id="element-864">
                <Card className="p-8 bg-white shadow-lg border-t-4 border-teal sticky top-24" data-id="element-865">
                  <h3 className="text-lg font-bold text-charcoal mb-4" data-id="element-866">
                    {c.ctaTitle}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6" data-id="element-867">{c.ctaDesc}</p>
                  <Link to="/contact" className="block" data-id="element-868">
                    <Button className="w-full" rightIcon={<ArrowRight size={18} data-id="element-870" />} data-id="element-869">
                      {c.bookCta}
                    </Button>
                  </Link>
                </Card>
              </Animate>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-871">
          <Animate data-id="element-872">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-coral/5 border-0" data-id="element-873">
              <h2 className="text-2xl font-bold mb-3" data-id="element-874">{c.ctaTitle}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-875">{c.ctaDesc}</p>
              <Link to="/contact" data-id="element-876">
                <Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-878" />} data-id="element-877">
                  {c.ctaCta}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}